/**
 * JourneyContext — the anonymous "student journey" primitive.
 *
 * A device-local UUID stitches every answer + visit a student gives
 * across the site into a single profile. The profile is mirrored on
 * the backend (server-shadow) so returning students survive a browser
 * wipe as long as they still hold the token elsewhere (e.g. via a
 * signed-in Google account binding, later).
 *
 * Nothing here forces sign-in. Sign-in is a *benefit* the user opts
 * into for cross-device sync and deadline reminders.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ANON_KEY = "wua:journey:anon";
const PROFILE_CACHE_KEY = "wua:journey:profile";

// Route → pillar map. Mirrors the JourneyRail STATIONS `.to` values
// so the "current stop" pulse on the tube map matches the URL the
// student is actually on. Keep in sync with:
//   Pick  → /beta/course-fit  (Course Fit)
//   Place → /cities/matcher   (City Matcher / any /cities/ page)
//   Prove → /admission        (Chance Calculator)
//   Apply → /shortlist        (final list) + legacy /compare
const PILLAR_ROUTES = {
  pick:  ["/beta/course-fit", "/explore", "/degree/", "/highest-earning",
          "/rankings", "/pathways", "/uk-fe-colleges"],
  place: ["/cities", "/countries/", "/uni/", "/flagships"],
  prove: ["/admission", "/grade-calculator", "/get-me-to",
          "/how-to-get-into", "/journal/"],
  apply: ["/shortlist", "/saved", "/compare", "/applications", "/clearing",
          "/employers", "/specialists"],
  // Map-only stop (Advanced Search). Done only once a server-issued match
  // receipt has been redeemed (advanced.searches_completed), never on visit.
  advanced: ["/advanced"],
};

// Routes we DON'T track (admin, ops, auth, legal utility).
const EXCLUDED_PREFIXES = [
  "/admin", "/uni-portal", "/firm-portal", "/counsellors",
  "/auth/callback", "/legal", "/claim", "/plus", "/wua-plus",
];

// Whole-segment routes, checked first: country guides (/apply) feed Place, and must not catch /applications.
const SEGMENT_ROUTES = { place: ["/apply"] };

function pillarForPath(path) {
  if (!path) return null;
  for (const [name, routes] of Object.entries(SEGMENT_ROUTES)) {
    if (routes.some((p) => path === p || path.startsWith(`${p}/`))) return name;
  }
  for (const [name, prefixes] of Object.entries(PILLAR_ROUTES)) {
    if (prefixes.some((p) => path.startsWith(p) || path.includes(p))) return name;
  }
  return null;
}

function shouldTrackPath(path) {
  return !EXCLUDED_PREFIXES.some((p) => path.startsWith(p));
}

function generateAnonId() {
  // RFC 4122 v4 via crypto when available; falls back to Math.random.
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function readAnonId() {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = generateAnonId();
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    // localStorage blocked (private browsing) — fall back to memory-only id.
    return generateAnonId();
  }
}

function rotateAnonId() {
  const id = generateAnonId();
  try {
    localStorage.setItem(ANON_KEY, id);
    localStorage.removeItem(PROFILE_CACHE_KEY);
    localStorage.removeItem(BOUND_KEY);
  } catch { /* memory-only */ }
  return id;
}

const BOUND_KEY = "wua:journey:bound";

function readCachedProfile() {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeCachedProfile(p) {
  try { localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

const JourneyContext = createContext(null);

export function JourneyProvider({ children }) {
  const location = useLocation();
  const [anonymousId, setAnonymousId] = useState(readAnonId);
  const { user, loading: authLoading } = useAuth();
  const prevUserRef = useRef(undefined);
  const [profile, setProfile] = useState(() => readCachedProfile() || {
    anonymous_id: null, what: {}, where: {}, why: {}, how: {},
    behaviour: { pillars_visited: [], first_seen: null, last_seen: null },
  });
  const [loaded, setLoaded] = useState(false);
  const inflightVisit = useRef(null);
  // Always the current id, so a slow request started before sign-in/bind can't write to (or apply) a stale copy.
  const anonIdRef = useRef(anonymousId);
  anonIdRef.current = anonymousId;
  const bindSentRef = useRef(new Set());
  const bindBusyRef = useRef(null);
  const [bindTick, setBindTick] = useState(0);
  const applyServerProfile = useCallback((p) => {
    if (!p) return;
    setProfile(p);
    writeCachedProfile(p);
    if (p.anonymous_id && p.anonymous_id !== anonIdRef.current) {
      try { localStorage.setItem(ANON_KEY, p.anonymous_id); } catch { /* ignore */ }
      anonIdRef.current = p.anonymous_id;
      setAnonymousId(p.anonymous_id);
    }
  }, []);

  // 1) Hydrate from backend once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/journey/profile", { params: { anonymous_id: anonymousId } });
        if (cancelled) return;
        setProfile(data);
        writeCachedProfile(data);
        // The server resolved a retired (merged) copy to the account's journey: use that id from now on.
        if (data?.anonymous_id && data.anonymous_id !== anonymousId) {
          try { localStorage.setItem(ANON_KEY, data.anonymous_id); } catch { /* ignore */ }
          setAnonymousId(data.anonymous_id);
        }
      } catch (e) {
        // A journey bound to an account isn't readable signed-out: start a fresh anonymous one.
        if (!cancelled && e?.response?.status === 403) { setAnonymousId(rotateAnonId()); return; }
      }
      finally { if (!cancelled) setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [anonymousId]);

  // 1b) Sign-in links this browser's journey to the account; sign-out starts a fresh one.
  useEffect(() => {
    if (authLoading) return;
    const prev = prevUserRef.current;
    prevUserRef.current = user ? user.user_id : null;
    if (!user) {
      if (prev) setAnonymousId(rotateAnonId());
      return;
    }
    let bound = null;
    try { bound = localStorage.getItem(BOUND_KEY); } catch { /* ignore */ }
    const key = `${user.user_id}:${anonymousId}`;
    // One bind at a time per account: when it lands, bindTick re-runs this check against the current id.
    if (bound === key || bindSentRef.current.has(key) || bindBusyRef.current === user.user_id) return;
    bindSentRef.current.add(key);
    bindBusyRef.current = user.user_id;
    const sameUser = () => prevUserRef.current === user.user_id;
    api.post("/journey/bind", { anonymous_id: anonymousId })
      .then(({ data }) => {
        if (!data?.anonymous_id) return;
        // The returned canonical id is already bound: never bind it again.
        bindSentRef.current.add(`${user.user_id}:${data.anonymous_id}`);
        const current = anonIdRef.current;
        // Apply only if this browser is still on the id we bound, or has already moved to the canonical one.
        if (!sameUser() || (current !== anonymousId && current !== data.anonymous_id)) return;
        try {
          localStorage.setItem(ANON_KEY, data.anonymous_id);
          localStorage.setItem(BOUND_KEY, `${user.user_id}:${data.anonymous_id}`);
        } catch { /* ignore */ }
        if (data.profile) { setProfile(data.profile); writeCachedProfile(data.profile); }
        if (data.anonymous_id !== current) setAnonymousId(data.anonymous_id);
      })
      .catch((e) => {
        bindSentRef.current.delete(key);
        if (sameUser() && anonIdRef.current === anonymousId && e?.response?.status === 403) setAnonymousId(rotateAnonId());
      })
      .finally(() => { bindBusyRef.current = null; setBindTick((t) => t + 1); });
  }, [user, authLoading, anonymousId, bindTick]);

  // 2) Record visits automatically on route change (debounced to avoid
  //    spamming the endpoint during quick nav bursts).
  useEffect(() => {
    if (!loaded) return;
    const path = location.pathname || "";
    if (!shouldTrackPath(path)) return;

    if (inflightVisit.current) clearTimeout(inflightVisit.current);
    inflightVisit.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/journey/visit", { anonymous_id: anonIdRef.current, path });
        if (data?.pillar) {
          setProfile((prev) => {
            const visited = new Set(prev?.behaviour?.pillars_visited || []);
            visited.add(data.pillar);
            const next = {
              ...prev,
              behaviour: {
                ...(prev?.behaviour || {}),
                pillars_visited: Array.from(visited),
                last_path: path,
                last_seen: new Date().toISOString(),
              },
            };
            writeCachedProfile(next);
            return next;
          });
        }
      } catch { /* fire-and-forget */ }
    }, 600);
    return () => { if (inflightVisit.current) clearTimeout(inflightVisit.current); };
  }, [location.pathname, anonymousId, loaded]);

  // 3) Public API — merge-patch the profile (and mirror to backend).
  const updateProfile = useCallback(async (patch) => {
    // Optimistic local update.
    setProfile((prev) => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(patch || {})) {
        next[k] = { ...(prev?.[k] || {}), ...(v || {}) };
      }
      writeCachedProfile(next);
      return next;
    });
    try {
      const { data } = await api.post("/journey/profile", { anonymous_id: anonIdRef.current, patch });
      applyServerProfile(data?.profile);
    } catch { /* best-effort */ }
  }, [applyServerProfile]);

  // 4) Mirror the compare-shortlist (localStorage) into the journey
  //    profile as a soft "considered" list. This does NOT gate PROVE
  //    completion — that requires an explicit /journey/confirm call.
  useEffect(() => {
    if (!loaded) return;
    const SHORTLIST_KEY = "wua:compareShortlist";
    const SHORTLIST_EVT = "wua:compareShortlist:change";
    let last = null;
    try { last = localStorage.getItem(SHORTLIST_KEY); } catch { /* ignore */ }
    const sync = () => {
      try {
        const raw = localStorage.getItem(SHORTLIST_KEY) || "[]";
        if (raw === last) return;
        last = raw;
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          updateProfile({ prove: { shortlist_considered: arr.filter((x) => typeof x === "string") } });
        }
      } catch { /* ignore */ }
    };
    window.addEventListener(SHORTLIST_EVT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(SHORTLIST_EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [loaded, updateProfile]);

  const pillarProgress = useMemo(() => {
    const rawCompleted = new Set(profile?.behaviour?.pillars_completed || []);
    // Implicit completion — if the student confirmed a LATER pillar,
    // they walked past every earlier one. Treat those as complete on
    // the tube map so we don't nag them with an "incomplete" dot
    // sitting between two green stops. Persistent state on the server
    // stays untouched; this is a UI-derivation only.
    const ORDER = ["pick", "place", "prove", "apply"];
    const highestIdx = ORDER.reduce(
      (max, k, i) => (rawCompleted.has(k) ? i : max),
      -1,
    );
    const completed = new Set(rawCompleted);
    for (let i = 0; i <= highestIdx; i += 1) completed.add(ORDER[i]);

    // Current pillar reflects ONLY the page you're on right now. On
    // non-pillar pages (home, legal, etc.) there's no current pillar.
    const current = pillarForPath(location.pathname);
    // Apply (applications) is its own stop after Shortlist; done once every application is exported.
    const onApplyPages = /^\/applications(\/|$)/.test(location.pathname || "");
    const shown = onApplyPages ? "applications" : current;
    return {
      pick:  { completed: completed.has("pick"),  current: shown === "pick"  },
      place: { completed: completed.has("place"), current: shown === "place" },
      prove: { completed: completed.has("prove"), current: shown === "prove" },
      apply: { completed: completed.has("apply"), current: shown === "apply" },
      applications: { completed: completed.has("applications"), current: onApplyPages },
      advanced: { completed: completed.has("advanced"), current: shown === "advanced" },
      currentPillar: shown,
      completedCount: completed.size,
    };
  }, [profile, location.pathname]);

  // Reset primitives — clear one pillar's artefact or the whole journey.
  const resetPillar = useCallback(async (pillar) => {
    try {
      const { data } = await api.post(`/journey/pillar/${pillar}/reset`,
                                       { anonymous_id: anonIdRef.current });
      applyServerProfile(data?.profile);
    } catch { /* best-effort */ }
  }, [applyServerProfile]);

  const resetJourney = useCallback(async () => {
    try {
      await api.post("/journey/reset", { anonymous_id: anonymousId });
      const empty = {
        anonymous_id: anonymousId,
        what: {}, where: {}, why: {}, how: {}, prove: {}, advanced: {}, persona: {},
        behaviour: { pillars_visited: [], pillars_completed: [] },
      };
      setProfile(empty);
      writeCachedProfile(empty);
    } catch { /* best-effort */ }
  }, [anonymousId]);

  // Confirm — the explicit "these are my picks" moment. Backend caps
  // and overwrites the confirmed array for the pillar, then recomputes
  // completion. Returns the fresh profile so the caller can chain a
  // toast or navigation without a follow-up fetch.
  const confirmPillar = useCallback(async (pillar, choices) => {
    try {
      const { data } = await api.post("/journey/confirm", {
        anonymous_id: anonIdRef.current,
        pillar,
        choices: Array.isArray(choices) ? choices : [],
      });
      applyServerProfile(data?.profile);
      return data;
    } catch (e) {
      return { ok: false, error: e?.message || "confirm failed" };
    }
  }, [applyServerProfile]);

  const value = useMemo(() => ({
    anonymousId, profile, updateProfile, pillarProgress, loaded,
    resetPillar, resetJourney, confirmPillar,
  }), [anonymousId, profile, updateProfile, pillarProgress, loaded,
      resetPillar, resetJourney, confirmPillar]);

  return <JourneyContext.Provider value={value}>{children}</JourneyContext.Provider>;
}

export function useJourney() {
  const ctx = useContext(JourneyContext);
  if (!ctx) throw new Error("useJourney must be used inside <JourneyProvider>");
  return ctx;
}

export { PILLAR_ROUTES };
